(() => {
  const ROOT_CLASS = 'moretti-inbox-hidden';
  const PURCHASE_CLASS = 'moretti-inbox-purchase-active';
  const PURCHASE_SELECTOR = '[id^="MainProduct-"] variant-selects, [id^="MainProduct-"] product-form';
  const mobile = window.matchMedia('(max-width: 749px)');
  const purchaseVisibility = new Map();
  let purchaseObserver;

  const observePurchaseControls = () => {
    if (!purchaseObserver) return;
    purchaseVisibility.forEach((visible, element) => {
      if (!element.isConnected) {
        purchaseObserver.unobserve(element);
        purchaseVisibility.delete(element);
      }
    });
    document.querySelectorAll(PURCHASE_SELECTOR).forEach((element) => {
      if (!purchaseVisibility.has(element)) {
        purchaseVisibility.set(element, false);
        purchaseObserver.observe(element);
      }
    });
  };
  const OVERLAY_ROOT_SELECTOR =
    'cart-drawer, cart-notification, quick-add-modal, product-modal, details-modal, [data-moretti-precheckout], dialog, [role="dialog"][aria-modal="true"]';
  const VISIBLE_MODAL_SELECTORS = [
    'cart-notification.active',
    '[data-moretti-precheckout]:not([hidden]):not([aria-hidden="true"])',
    'quick-add-modal[open]',
    'product-modal[open]',
    'dialog[open]',
    '[role="dialog"][aria-modal="true"]',
  ];

  const isVisible = (element) => {
    if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;

    if (typeof element.checkVisibility === 'function') {
      return element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
    }

    let current = element;
    while (current) {
      if (current.hidden || current.getAttribute?.('aria-hidden') === 'true') return false;

      const style = window.getComputedStyle(current);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.visibility === 'collapse' ||
        Number(style.opacity) === 0
      ) {
        return false;
      }

      current = current.parentElement;
    }

    return element.getClientRects().length > 0;
  };

  const hasCommerceOverlay = () => {
    if (document.querySelector('cart-drawer.active')) return true;
    if (document.body?.classList.contains('overflow-hidden')) return true;
    if (document.body?.classList.contains('overflow-hidden-tablet') && matchMedia('(max-width: 989px)').matches) return true;
    if (document.body?.classList.contains('overflow-hidden-mobile') && matchMedia('(max-width: 749px)').matches) return true;

    return Array.from(document.querySelectorAll(VISIBLE_MODAL_SELECTORS.join(','))).some(isVisible);
  };

  let scheduled = false;

  const syncLauncherVisibility = () => {
    scheduled = false;
    document.documentElement.classList.toggle(ROOT_CLASS, hasCommerceOverlay());
    document.documentElement.classList.toggle(
      PURCHASE_CLASS,
      mobile.matches && Array.from(purchaseVisibility.values()).some(Boolean)
    );
  };

  const scheduleSync = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(syncLauncherVisibility);
  };

  const handleOverlayTransition = (event) => {
    if (event.target instanceof Element && event.target.closest(OVERLAY_ROOT_SELECTOR)) {
      scheduleSync();
    }
  };

  const start = () => {
    if ('IntersectionObserver' in window) {
      purchaseObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => purchaseVisibility.set(entry.target, entry.isIntersecting));
        scheduleSync();
      });
      observePurchaseControls();
    }
    mobile.addEventListener?.('change', scheduleSync);
    syncLauncherVisibility();

    const observer = new MutationObserver((records) => {
      let relevant = false;
      let purchaseChanged = false;
      const visited = new Set();
      for (const record of records) {
        if (relevant && purchaseChanged) break;
        if (record.type === 'attributes') {
          if (!relevant) {
            relevant = record.target === document.body ||
              (record.target instanceof Element && Boolean(record.target.closest(OVERLAY_ROOT_SELECTOR)));
          }
          continue;
        }
        if (record.type !== 'childList') continue;
        for (const nodes of [record.addedNodes, record.removedNodes]) {
          for (const node of nodes) {
            if (!(node instanceof Element) || visited.has(node)) continue;
            visited.add(node);
            if (!relevant && node.matches(OVERLAY_ROOT_SELECTOR)) relevant = true;
            if (!purchaseChanged && node.matches('variant-selects, product-form')) purchaseChanged = true;
            if (node.firstElementChild) {
              if (!relevant && node.querySelector(OVERLAY_ROOT_SELECTOR)) relevant = true;
              if (!purchaseChanged && node.querySelector('variant-selects, product-form')) purchaseChanged = true;
            }
            if (relevant && purchaseChanged) break;
          }
          if (relevant && purchaseChanged) break;
        }
      }
      if (purchaseChanged) observePurchaseControls();
      if (relevant || purchaseChanged) scheduleSync();
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'open', 'aria-hidden', 'aria-modal'],
    });

    document.addEventListener('toggle', handleOverlayTransition, true);
    document.addEventListener('transitionend', handleOverlayTransition, true);
    document.addEventListener('animationend', handleOverlayTransition, true);
    document.addEventListener('shopify:section:load', () => {
      observePurchaseControls();
      scheduleSync();
    });
    window.addEventListener('pageshow', scheduleSync);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();


// Keep the optional welcome teaser out of the way when the footer signup is visible.
// This targets only the teaser button, preserving the open Forms modal and signup flow.
(() => {
  const start = () => {
    const signup = document.querySelector('.moretti-newsletter__form');
    if (!signup || !('IntersectionObserver' in window)) return;
    let visible = false, host, waiting;
    const attribute = 'data-moretti-footer-signup-visible';
    const sync = () => host?.toggleAttribute(attribute, visible);
    const observer = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .15;
      sync();
    }, { threshold: [0, .15] });
    observer.observe(signup);
    const attach = () => {
      const candidate = document.querySelector('shopify-forms-embed#app-embed-container-1058877');
      if (!candidate?.shadowRoot) return false;
      host = candidate;
      const root = candidate.shadowRoot;
      const ensureStyle = () => {
        if (root.querySelector('style[data-moretti-footer-teaser]')) return;
        const style = document.createElement('style');
        style.dataset.morettiFooterTeaser = '';
        style.textContent = ':host([' + attribute + ']) button[class*="teaserContainer"]{display:none!important}';
        root.append(style);
      };
      ensureStyle();
      new MutationObserver(ensureStyle).observe(root, { childList: true });
      sync();
      waiting?.disconnect();
      return true;
    };
    if (!attach()) {
      waiting = new MutationObserver(attach);
      waiting.observe(document.body, { childList: true, subtree: true });
      customElements.whenDefined('shopify-forms-embed').then(attach);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
